import * as React from 'react';
import { css } from 'react-strict-dom';

import { Api, useApiCallable } from '../../../api';
import { Button, Checkbox, Stack, Text } from '../../../components';
import { color } from '../../../constants/color.css';
import { Model } from '../../../models';
import { formatAsISO, relationsAsIri } from '../../../util';
import CommentsThread from '../../comments/CommentsThread';
import TimelineEvent from './TimelineEvent';

type Props = Readonly<{
  ticket: Api.Ticket.V_Detail;
  showInternal: boolean;
  invalidate: () => unknown;
  companySlug: string;
  allUsers: ReadonlyArray<Api.User.BaseView>;
}>;

/**
 * The ticket detail thread: the shared comments thread plus the ticket-only
 * composer controls (Close/Reopen and the info-needed/resolution checkboxes).
 */
export default function TicketCommentsThread({
  ticket,
  showInternal,
  invalidate,
  companySlug,
  allUsers,
}: Props) {
  const callApi = useApiCallable();

  const infoNeededLabel = ticket.labels.find(Model.TicketLabel.isInfoNeeded);
  const resolutionLabel = ticket.labels.find(Model.TicketLabel.isResolution);

  const [removeInfoNeeded, setRemoveInfoNeeded] = React.useState(false);
  const [removeResolution, setRemoveResolution] = React.useState(false);
  const [updatingClosedAt, setUpdatingClosedAt] = React.useState(false);

  const toggleTicketClosed = async () => {
    if (updatingClosedAt) return;
    setUpdatingClosedAt(true);
    await callApi(`/tickets/${ticket.id}`, {
      method: 'PUT',
      jsonBody: {
        closedAt: ticket.closedAt ? null : formatAsISO(new Date()),
      },
    });
    setUpdatingClosedAt(false);
    invalidate();
  };

  const updateLabels = async () => {
    if (!removeInfoNeeded && !removeResolution) return;
    const newLabels = ticket.labels.filter((label) => {
      if (removeInfoNeeded && label.id === infoNeededLabel?.id) {
        return false;
      }
      if (removeResolution && label.id === resolutionLabel?.id) {
        return false;
      }
      return true;
    });
    await callApi(`/tickets/${ticket.id}`, {
      method: 'PUT',
      jsonBody: {
        labels: relationsAsIri(newLabels),
      },
    });
  };

  return (
    <CommentsThread
      entity={{
        body: ticket.body,
        uploads: ticket.uploads,
        author: ticket.createdBy,
        createdAt: ticket.createdAt,
        comments: ticket.comments,
        events: ticket.events,
      }}
      showInternal={showInternal}
      invalidate={invalidate}
      composer={{
        endpoint: '/comments',
        relationKey: 'ticket',
        relationValue: `/tickets/${ticket.id}`,
        extraControls: (
          <>
            {infoNeededLabel && (
              <Stack padH={1} padV={1} style={styles.card} gap={0.25}>
                <Text typeface="body.medium">Remove info needed status?</Text>
                <Checkbox
                  label={
                    <Text>
                      I have provided all the information that has been
                      requested
                    </Text>
                  }
                  checked={removeInfoNeeded}
                  onChange={setRemoveInfoNeeded}
                />
              </Stack>
            )}

            {resolutionLabel && (
              <Stack padH={1} padV={1} style={styles.card} gap={0.25}>
                <Text typeface="body.medium">Remove resolved status?</Text>
                <Checkbox
                  label={
                    <Text>
                      I've explained in my comment why I don't agree with this
                      ticket's resolution
                    </Text>
                  }
                  checked={removeResolution}
                  onChange={setRemoveResolution}
                />
              </Stack>
            )}
          </>
        ),
        extraActions: ({ submit, bodyValue }) => (
          <Button
            label={ticket.closedAt ? 'Reopen Ticket' : 'Close Ticket'}
            colorScheme="black"
            disabled={updatingClosedAt}
            style={styles.fullWidthOnMobile}
            onClick={async () => {
              await toggleTicketClosed();
              if (bodyValue !== '') submit();
            }}
          />
        ),
        onPosted: updateLabels,
      }}
      renderEvents={(events) => (
        <TimelineEvent
          events={events}
          companySlug={companySlug}
          allUsers={allUsers}
        />
      )}
    />
  );
}

const styles = css.create({
  fullWidthOnMobile: {
    width: {
      default: 'auto',
      '@media (max-width: 768px)': '100%',
    },
  },
  card: {
    backgroundColor: color.blueBg,
    borderRadius: 12,
  },
});
